// Live state of the chat list (mobile pro/proChatLive.js): "typing…" in place of
// the last message and the online dot on a person's avatar, from events the
// backend already sends. Nothing is guessed: a row shows the dot or "typing…"
// only after the matching socket event.
//   typing   POST /group/:id/checkIsTyping blasts `{ fullName, typingData: [{ user,
//            group, isTyping, isOneToOne, typingUserFullName }] }` under the group's
//            id as the event name. An event is for me when an entry names me (the
//            typist gets no entry). Like the chat screens, it clears on the typist's
//            isTyping false, else 6 s after the last event (utils/chatTyping).
//   online   `userStatus { user, isOnline }` on connect / disconnect. The list
//            payload has no presence, so a dot appears with the person's next change.
// The listener uses socket.onAny, so the chat panes' Socket.off(event) (which drops
// every listener of that event) never removes it.
import { useEffect, useSyncExternalStore } from "react";
import { TYPING_CLEAR_MS } from "../../utils/chatTyping";

const TYPING_MS = TYPING_CLEAR_MS;

const typing = new Map(); // groupId -> { name }
const online = new Map(); // userId -> true
const timers = new Map(); // groupId -> timeout
const listeners = new Set();
let watched = new Set(); // ids of the people in the listed direct chats

const emit = () => listeners.forEach((l) => l());
const subscribe = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const key = (v) => (v == null || v === "" ? null : String(v));

function watchUsers(ids) {
  watched = new Set((ids || []).map(key).filter(Boolean));
  let changed = false;
  online.forEach((_, id) => {
    if (!watched.has(id)) {
      online.delete(id);
      changed = true;
    }
  });
  if (changed) emit();
}

function clearTyping(gid) {
  clearTimeout(timers.get(gid));
  timers.delete(gid);
  if (typing.delete(gid)) emit();
}

function handleEvent(meId, event, data) {
  if (!data || typeof data !== "object") return;
  if (event === "userStatus") {
    const id = key(data.user);
    if (!id || !watched.has(id) || id === key(meId)) return;
    const now = !!data.isOnline;
    if (now === online.has(id)) return;
    if (now) online.set(id, true);
    else online.delete(id);
    emit();
    return;
  }
  if (!Array.isArray(data.typingData) || !data.typingData.length) return;
  const me = key(meId);
  const mine = data.typingData.find((d) => d && key(d.user) === me);
  if (!mine) return;
  const gid = key(mine.group) || key(event);
  if (!gid) return;
  if (!mine.isTyping || mine.isTyping === "false") {
    clearTyping(gid);
    return;
  }
  const name = String(data.fullName || mine.typingUserFullName || "").trim();
  const prev = typing.get(gid);
  if (!prev || prev.name !== name) {
    typing.set(gid, { name });
    emit();
  }
  clearTimeout(timers.get(gid));
  timers.set(gid, setTimeout(() => clearTyping(gid), TYPING_MS));
}

function resetLive() {
  timers.forEach((tm) => clearTimeout(tm));
  timers.clear();
  const had = typing.size > 0 || online.size > 0;
  typing.clear();
  online.clear();
  if (had) emit();
}

/**
 * Owner hook (LatestMessages): listens on the app socket while the chat list is
 * shown (onRoute and chatView). partnerKey = the listed people's ids joined by
 * "," (a string, so the effect only reruns when they change).
 */
export function useChatListSocket({ socket, onRoute, chatView, user, partnerKey }) {
  const enabled = !!(onRoute && chatView);
  const meId = user ? user.id : null;
  useEffect(() => {
    if (!enabled) return undefined;
    watchUsers(partnerKey ? String(partnerKey).split(",") : []);
    return undefined;
  }, [enabled, partnerKey]);
  useEffect(() => {
    if (!enabled || !socket || typeof socket.onAny !== "function") return undefined;
    const handler = (event, data) => handleEvent(meId, event, data);
    socket.onAny(handler);
    return () => {
      if (typeof socket.offAny === "function") socket.offAny(handler);
      resetLive();
    };
  }, [socket, enabled, meId]);
}

/** Row hook: the typist of a chat ({ name } or null) and whether its person is online. */
export function useChatLive(groupId, partnerId) {
  const gid = key(groupId);
  const uid = key(partnerId);
  const typingNow = useSyncExternalStore(subscribe, () => (gid ? typing.get(gid) || null : null));
  const onlineNow = useSyncExternalStore(subscribe, () => (uid ? online.has(uid) : false));
  return { typing: typingNow, online: onlineNow };
}
